import { UiVersion } from "../../uiVersion";

// GUNDI-5639: the URL must always wear the ACTIVE version's vocabulary, so
// switching versions TRANSLATES the path instead of reusing it. Never leave a
// legacy user on a URL that only means something in new vocabulary, or the
// other way round.
//
// Entity-level only: tabs and deep sub-paths are dropped, since the two trees
// do not have the same sub-pages. Lists and the vocabulary-neutral pages
// (`/`, `/sources*`, `/workspaces*`, `/account`) map to themselves.
export type VersionPathResolvers = {
    // Legacy `/connections/:providerId` is what the new UI calls a route.
    routeIdForProvider: (providerId: string) => Promise<string | null>;
    // A new-UI route maps back to the provider integration the legacy
    // connection screen was keyed on.
    providerIdForRoute: (routeId: string) => Promise<string | null>;
};

export async function mapPathAcrossVersions(
    pathname: string,
    target: UiVersion,
    resolvers: VersionPathResolvers,
): Promise<string> {
    const [head, id] = pathname.split("/").filter(Boolean);

    if (target === "new") {
        // Leaving legacy.
        if (head === "connections") {
            if (!id) return "/";
            if (id === "new") return "/routes/new";
            const routeId = await resolvers.routeIdForProvider(id);
            // No route (or the lookup failed): the connection page still
            // exists in the new UI and links to the route, one click away.
            return routeId ? `/routes/${routeId}` : `/connections/${id}`;
        }
        if (head === "destinations" || head === "destination") {
            if (!id) return "/connections";
            return id === "new" ? "/connections/new" : `/connections/${id}`;
        }
        return pathname;
    }

    // Leaving the new UI.
    if (head === "routes") {
        if (!id) return "/";
        if (id === "new") return "/connections/new";
        const providerId = await resolvers.providerIdForRoute(id);
        // A route with no resolvable provider has no legacy equivalent at all.
        return providerId ? `/connections/${providerId}` : "/";
    }
    if (head === "connections") {
        if (!id) return "/destinations";
        return id === "new" ? "/destinations/new" : `/destinations/${id}`;
    }
    return pathname;
}
